import _ from 'underscore';
import classNames from 'classnames';
import React, { useCallback, useState, useMemo, useRef, useEffect } from 'react';
import {
  FlatTree,
  TreeItem,
  TreeItemLayout,
  // eslint-disable-next-line camelcase
  useHeadlessFlatTree_unstable,
  mergeClasses,
  Tooltip,
  Spinner,
} from '@fluentui/react-components';
import { ChevronRightRegular, ChevronDownRegular } from '@fluentui/react-icons';
import { SearchBox, Checkbox } from '@fluentui/react';

import { buildMap, filterTreeItems } from './utils';
import { IDropdownTreeSelectProps, CustomTreeItemProps } from './types';
import { useDropdownTreeStyles } from './styles';

const CustomTreeItem = React.memo(({
  flatTreeItem,
  checked,
  onCheckedChange,
  onRenderContent,
  styles,
  isOpen,
  isLoading,
  lazyLoadingEnabled,
} : CustomTreeItemProps) => {
  const treeItemsProps = flatTreeItem.getTreeItemProps();
  const {
    content,
    allowedCheck,
    parentValue,
    ...treeItemProps
  } = treeItemsProps;

  const itemType = useMemo(() => {
    if (lazyLoadingEnabled && treeItemsProps['aria-level'] === 1) {
      return 'branch';
    }
    return treeItemProps.itemType;
  }, [lazyLoadingEnabled, treeItemProps.itemType, treeItemsProps]);

  // we have a very weird issue here that the dropdown will close when we click on the icon
  // so we need to stop the propagation of the event for some elements
  const stopPropagation = useCallback((e) => {
    e.stopPropagation();
  }, []);
  const expandIcon = useMemo(() => {
    if (lazyLoadingEnabled && treeItemsProps['aria-level'] === 1) {
      if (isLoading) {
        return (<Spinner size="tiny" onClick={stopPropagation} />);
      } else if (isOpen) {
        return (<ChevronDownRegular />);
      }
      return (<ChevronRightRegular />);
    }

    return undefined;
  }, [isLoading, isOpen, lazyLoadingEnabled, stopPropagation, treeItemsProps]);
  const onChange = useCallback((e, isChecked) => {
    onCheckedChange({
      key: flatTreeItem.value,
      value: isChecked,
      content,
      parentValue,
    });
  }, [content, flatTreeItem.value, onCheckedChange, parentValue]);

  return (
    <TreeItem {...treeItemProps} itemType={itemType} onClick={stopPropagation}>
      <TreeItemLayout
        className={styles.treeItemLayout}
        expandIcon={expandIcon}
      >
        {
          allowedCheck
            ? (
              <Checkbox
                label={content}
                checked={checked}
                className="ba-dropdown-tree-select-checkbox"
                onChange={onChange}
                {...(_.isFunction(onRenderContent) ? { onRenderLabel: () => onRenderContent(flatTreeItem) } : {})}
              />
            )
            : <div className={styles.branch}>{_.isFunction(onRenderContent) ? onRenderContent(flatTreeItem) : content}</div>
        }
      </TreeItemLayout>
    </TreeItem>
  );
});

export const DropdownTreeSelect = ({
  placeholder,
  items,
  checkedItems,
  onChange,
  openItems,
  onOpenChange,
  onRenderContent,
  onRenderTooltip,
  customFilter,
  lazyLoad,
  isMultiSelect,
  ...restProps
}: IDropdownTreeSelectProps) => {
  const containerRef = useRef<HTMLDivElement>(null);
  const styles = useDropdownTreeStyles();

  const [searchValue, setSearchValue] = useState('');
  const [isResultOpen, setIsResultOpen] = useState(false);
  const lazyLoadingEnabled = !!lazyLoad;
  const [lazyLoadedData, setLazyLoadedData] = useState([]);
  const [lazyLoadedParents, setLazyLoadedParents] = useState([]);

  const onChangeSearchValue = useCallback((e, newValue = '') => {
    const trimmedValue = newValue.trim();
    setSearchValue(trimmedValue);
  }, []);

  const itemsToUse = useMemo(() => ([...items, ...lazyLoadedData]), [items, lazyLoadedData]);
  const map = useMemo(() => buildMap(itemsToUse), [itemsToUse]);
  const filteredTreeItems = useMemo(() => filterTreeItems(itemsToUse, map, searchValue, customFilter), [map, searchValue, itemsToUse, customFilter]);

  const parentsWithChild = useMemo(() => {
    if (!lazyLoad) {
      return [];
    }
    return _.chain(itemsToUse)
      .map(item => item.parentValue)
      .compact()
      .uniq()
      .value();
  }, [itemsToUse, lazyLoad]);
  const parentsWithChildOrLoaded = useMemo(() => _.uniq([...parentsWithChild, ...lazyLoadedParents]), [parentsWithChild, lazyLoadedParents]);
  const onOpenChangeInternal = useCallback((e, data) => {
    onOpenChange(Array.from(data.openItems));

    if (_.isFunction(lazyLoad)) {
      const [openedItem] = _.difference([...data.openItems], openItems);
      if (openedItem && !_.includes(parentsWithChildOrLoaded, openedItem)) {
        lazyLoad(openedItem)
          .then((itemChildData) => {
            setLazyLoadedData([...lazyLoadedData, ...itemChildData]);
          })
          .finally(() => {
            setLazyLoadedParents([...lazyLoadedParents, openedItem]);
          });
      }
    }
  }, [lazyLoad, lazyLoadedData, lazyLoadedParents, onOpenChange, openItems, parentsWithChildOrLoaded]);

  const onItemChecked = useCallback(({
    key,
    value,
    content,
    parentValue,
  }) => {
    if (!isMultiSelect) {
      onChange([{
        value: key,
        parentValue,
        content,
      }]);
      return;
    }

    if (value) {
      onChange([...checkedItems, key]);
    } else {
      onChange(checkedItems.filter(item => item !== key));
    }
  }, [checkedItems, isMultiSelect, onChange]);

  const flatTree = useHeadlessFlatTree_unstable(filteredTreeItems, { openItems, onOpenChange: onOpenChangeInternal });

  useEffect(() => {
    const handleBlur = (e: MouseEvent) => {
      if (containerRef.current && !containerRef.current.contains(e.target as Node)) {
        setTimeout(() => {
          setIsResultOpen(false);
        }, 10);
      }
    };
    document.addEventListener('click', handleBlur);
    return () => {
      document.removeEventListener('click', handleBlur);
    };
  }, []);

  const showResults = useCallback(() => {
    setIsResultOpen(true);
  }, []);

  return (
    <div className={classNames(styles.root, 'ba-dropdown-tree-select')} ref={containerRef}>
      <SearchBox
        placeholder={placeholder}
        onChange={onChangeSearchValue}
        onFocus={showResults}
        className={classNames('ba-dropdown-tree-select-search-box')}
      />
      <div className={classNames('ba-dropdown-tree-select-dropdown', mergeClasses(styles.dropdownContent, !isResultOpen && styles.hideContent))}>
        {
          filteredTreeItems.length
            ? (
              <FlatTree {...flatTree.getTreeProps()} aria-label="Selection" {...restProps}>
                {
                  Array.from(flatTree.items(), (flatTreeItem : any) => {
                    const treeItemsProps = flatTreeItem.getTreeItemProps();
                    const isOpen = _.includes(openItems, flatTreeItem.value);
                    const isLoading = isOpen && lazyLoadingEnabled && !_.includes(parentsWithChildOrLoaded, flatTreeItem.value);

                    if (treeItemsProps.showTips) {
                      return (
                        <Tooltip
                          key={flatTreeItem.value}
                          withArrow
                          positioning="below"
                          content={_.isFunction(onRenderTooltip) ? onRenderTooltip(treeItemsProps) : null}
                          relationship="description"
                        >
                          <div className="custom-tree-item">
                            <CustomTreeItem
                              flatTreeItem={flatTreeItem}
                              checked={checkedItems.includes(flatTreeItem.value)}
                              onCheckedChange={onItemChecked}
                              onRenderContent={onRenderContent}
                              styles={styles}
                              isOpen={isOpen}
                              isLoading={isLoading}
                              lazyLoadingEnabled={lazyLoadingEnabled}
                            />
                          </div>
                        </Tooltip>
                      );
                    }

                    return (
                      <div key={flatTreeItem.value}>
                        <CustomTreeItem
                          key={flatTreeItem.value}
                          flatTreeItem={flatTreeItem}
                          checked={checkedItems.includes(flatTreeItem.value)}
                          onCheckedChange={onItemChecked}
                          onRenderContent={onRenderContent}
                          styles={styles}
                          isOpen={isOpen}
                          isLoading={isLoading}
                          lazyLoadingEnabled={lazyLoadingEnabled}
                        />
                      </div>
                    );
                  })
                }
              </FlatTree>
            )
            : (<div className={styles.noResults}>no results</div>)
        }
      </div>
    </div>
  );
};

DropdownTreeSelect.defaultProps = {
  placeholder: '',
  items: [],
  checkedItems: [],
  onChange: _.noop,
  openItems: [],
  onOpenChange: _.noop,
  onRenderContent: null,
  onRenderTooltip: null,
  customFilter: undefined,
  isMultiSelect: true,
};
